import { COOKIE_KEY as DEVICE_ID_COOKIE } from '@1debit/device-intelligence';
import { UnitType } from '@1debit/chime-schemas/typescript_web_analytics';
import { ANON_ID_COOKIE } from 'utils/cookies';

interface StatsigInlineTrackerProps {
  experimentKey: string;
  variantKey: string;
  nonce?: string;
}

/**
 * Server-rendered inline <script> that fires the experiment_bucketed Hawker
 * event and POSTs to /api/statsig-exposure. Runs pre-hydration so it survives
 * Meta in-app browser hydration failures (Facebook/Instagram inject DOM that
 * breaks React hydration). Mirrors NinetailedInlineTracker.
 *
 * Reads the Chime device-ID cookie inside the script — the same identifier
 * the StatsigExperimentAdapter uses to bucket — not the Segment anonymous ID.
 */
export function StatsigInlineTracker({ experimentKey, variantKey, nonce }: StatsigInlineTrackerProps) {
  // Escape `<` as `<` so any `</script>` substring in the data can't
  // break out of the inline script tag.
  const trackingData = JSON.stringify({
    unit_type: UnitType.UNIT_TYPE_ANONYMOUS,
    experiment_key: experimentKey,
    variant_key: variantKey,
  }).replace(/</g, '\\u003c');
  const cookies = JSON.stringify({
    deviceCookie: DEVICE_ID_COOKIE,
    anonCookie: ANON_ID_COOKIE,
  }).replace(/</g, '\\u003c');

  return (
    <script
      nonce={nonce}
      suppressHydrationWarning
      data-statsig-exposure-tracker={experimentKey}
      dangerouslySetInnerHTML={{
        __html: `
(function() {
  var data = ${trackingData};
  var cookies = ${cookies};

  function getCookieValue(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }

  var deviceId = getCookieValue(cookies.deviceCookie);
  // Without a device ID we cannot match the StatsigUser used for bucketing;
  // skip rather than emit a meaningless exposure.
  if (!deviceId) return;
  var segmentAnonId = getCookieValue(cookies.anonCookie);

  var attempts = 0;
  function fire() {
    attempts++;
    if (window.Track && window.analyticsHandler) {
      window.analyticsHandler.trackEvent(window.Track.WebActionTriggered, {
        unique_id: 'experiment_bucketed',
        item_details: {
          experiment_bucketed: {
            unit_id: deviceId,
            unit_type: data.unit_type,
            experiment_key: data.experiment_key,
            variant_key: data.variant_key
          }
        }
      });
    } else if (attempts < 3000) {
      setTimeout(fire, 1);
    } else {
      console.warn('[StatsigInlineTracker] Analytics SDK not available after 3000 attempts, experiment_bucketed not sent for: ' + data.experiment_key);
    }
  }
  fire();

  try {
    fetch('/api/statsig-exposure', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experimentId: data.experiment_key,
        variantKey: data.variant_key,
        anonymousId: deviceId,
        segmentAnonymousId: segmentAnonId,
        url: window.location.href
      }),
      keepalive: true
    }).catch(function() {});
  } catch (e) {}
})();
`,
      }}
    />
  );
}
